import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useProducts } from './ProductsContext';
import { useSiteSettings } from './SiteSettingsContext';
import { track } from '../analytics/track';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = 'essenya_cart_v2';

export function CartProvider({ children }) {
  const { getProduct } = useProducts();
  const { guardPurchase } = useSiteSettings();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const [discountCode, setDiscountCode] = useState(() => localStorage.getItem('essenya_discount') || '');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem('essenya_discount', discountCode || ''); }, [discountCode]);

  const addItem = (productId, quantity = 1) => {
    if (guardPurchase()) return;
    setItems(prev => {
      const idx = prev.findIndex(i => i.product_id === productId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, { product_id: productId, quantity }];
    });
    setIsOpen(true);
    const p = getProduct(productId);
    track('add_to_cart', { product_id: productId, value: p?.price, currency: 'EUR' });
  };
  const updateQty = (pid, qty) => setItems(prev => prev.map(i => i.product_id === pid ? { ...i, quantity: Math.max(1, qty) } : i));
  const removeItem = (pid) => setItems(prev => prev.filter(i => i.product_id !== pid));
  const clear = () => { setItems([]); setDiscountCode(''); };

  const lines = useMemo(
    () => items.map(i => ({ ...i, product: getProduct(i.product_id) })).filter(l => l.product),
    [items, getProduct]
  );
  const subtotal = useMemo(() => lines.reduce((s, l) => s + l.product.price * l.quantity, 0), [lines]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const checkout = async () => {
    const payload = lines
      .filter(l => l.product?.variant_id)
      .map(l => ({ variant_id: l.product.variant_id, quantity: l.quantity }));
    if (!payload.length) throw new Error('empty');
    const r = await axios.post(`${API}/shop/checkout`, { lines: payload, discount_code: discountCode || null });
    track('begin_checkout', { value: subtotal, currency: 'EUR' });
    window.location.href = r.data.checkout_url;
  };

  // Checkout with explicit lines (avoids stale state for 1-click buy)
  const checkoutWithLines = async (explicitLines) => {
    const payload = (explicitLines || [])
      .filter(l => l.variant_id)
      .map(l => ({ variant_id: l.variant_id, quantity: l.quantity || 1 }));
    if (!payload.length) throw new Error('empty');
    const r = await axios.post(`${API}/shop/checkout`, { lines: payload, discount_code: discountCode || null });
    track('begin_checkout', { value: null, currency: 'EUR' });
    window.location.href = r.data.checkout_url;
  };

  // Bundle checkout: fixed price via dynamic one-time Shopify discount
  const bundleCheckout = async (bundleLines) => {
    const payload = (bundleLines || [])
      .filter(l => l.variant_id)
      .map(l => ({ variant_id: l.variant_id, quantity: l.quantity || 1, price: l.price }));
    if (!payload.length) throw new Error('empty');
    const r = await axios.post(`${API}/shop/bundle-checkout`, { lines: payload });
    track('bundle_complete', { value: 20.99, currency: 'EUR' });
    track('begin_checkout', { value: 20.99, currency: 'EUR' });
    window.location.href = r.data.checkout_url;
  };

  // Proprietary checkout (Emergent + Stripe) — server prices from DB catalog
  const startCheckout = async (customer, opts = {}) => {
    if (guardPurchase()) return;
    const source = opts.items && opts.items.length ? opts.items : lines;
    const items = source.filter(l => l.product_id).map(l => ({ product_id: l.product_id, quantity: l.quantity || 1 }));
    if (!items.length) throw new Error('empty');
    const r = await axios.post(`${API}/checkout/session`, {
      items,
      origin_url: window.location.origin,
      discount_code: opts.isBundle ? null : (discountCode || null),
      customer,
      is_bundle: !!opts.isBundle,
    });
    track('begin_checkout', { value: opts.isBundle ? 20.99 : subtotal, currency: 'EUR' });
    window.location.href = r.data.url;
  };

  return (
    <CartContext.Provider value={{ items, lines, subtotal, count, addItem, updateQty, removeItem, clear, isOpen, setIsOpen, checkout, checkoutWithLines, bundleCheckout, startCheckout, discountCode, setDiscountCode }}>
      {children}
    </CartContext.Provider>
  );
}
